import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { IntegrationPF } from "app/features/production/models/intergration-pf.model";
import { SnackService } from "app/shared/services/snack-service.service";
import { environment } from "environments/environment";
import { BehaviorSubject, Observable } from "rxjs";
import { OF } from "../models/of.model";
import { Poste } from "app/features/production/models/poste.model";
import { ChangementLot } from "app/features/production/models/changement-lot.model";
import { PosteInterval } from "app/features/production/models/poste-interval.model";
import { NotificationsService } from "app/features/notifications/services/notification.service";
import { DocumentModel } from "app/features/documents/models/document.model";
import { safeFirstValueFrom } from "../../../shared/utils/safe-first-value-from";

@Injectable({
  providedIn: "root",
})
export class OfService {
  private _ofsPlannified$ = new BehaviorSubject<OF[]>([]);
  private _ofs$ = new BehaviorSubject<OF[]>([]);
  private _ofSaving$ = new BehaviorSubject<boolean>(false);
  private _ofSaved$ = new BehaviorSubject<boolean>(false);
  private _ofLoading$ = new BehaviorSubject<boolean>(false);
  private _ofLoaded$ = new BehaviorSubject<boolean>(false);
  private _integrators$ = new BehaviorSubject<IntegrationPF[]>([]);
  public _currentPoste$ = new BehaviorSubject<Poste>(null);
  private _currentChangements$ = new BehaviorSubject<ChangementLot[]>([]);
  private _postes$ = new BehaviorSubject<Poste[]>([]);
  private _editing$ = new BehaviorSubject<boolean>(false);
  private _saving$ = new BehaviorSubject<boolean>(false);
  private _saved$ = new BehaviorSubject<boolean>(false);
  private _currentIntervalsPoste$ = new BehaviorSubject<PosteInterval[]>([]);
  private _prod_nav$ = new BehaviorSubject<{ fo: OF; doc: DocumentModel }[]>(
    [],
  );

  private _refreshOf$ = new BehaviorSubject<number>(0);

  mock = [];

  get refreshOf(): Observable<number> {
    return this._refreshOf$.asObservable();
  }

  get ofs$(): Observable<OF[]> {
    return this._ofs$.asObservable();
  }

  get prod_nav$(): Observable<{ fo: OF; doc: DocumentModel }[]> {
    return this._prod_nav$.asObservable();
  }

  get ofsPlannified$(): Observable<OF[]> {
    return this._ofsPlannified$.asObservable();
  }

  get integrators$(): Observable<IntegrationPF[]> {
    return this._integrators$.asObservable();
  }

  get ofSaving$(): Observable<boolean> {
    return this._ofSaving$.asObservable();
  }

  get ofSaved$(): Observable<boolean> {
    return this._ofSaved$.asObservable();
  }

  get ofLoading$(): Observable<boolean> {
    return this._ofLoading$.asObservable();
  }

  get ofLoaded$(): Observable<boolean> {
    return this._ofLoaded$.asObservable();
  }

  get currentPoste$(): Observable<Poste> {
    return this._currentPoste$.asObservable();
  }

  get currentChangements$(): Observable<ChangementLot[]> {
    return this._currentChangements$.asObservable();
  }

  get currentIntervalsPoste$(): Observable<PosteInterval[]> {
    return this._currentIntervalsPoste$.asObservable();
  }

  get postes$(): Observable<Poste[]> {
    return this._postes$.asObservable();
  }

  get editing$(): Observable<boolean> {
    return this._editing$.asObservable();
  }

  get saving$(): Observable<boolean> {
    return this._saving$.asObservable();
  }

  get saved$(): Observable<boolean> {
    return this._saved$.asObservable();
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private snackService: SnackService,
    private notifService: NotificationsService,
  ) {}

  selectPoste(s: Poste) {
    this._currentPoste$.next(s);
  }

  deletePoste(p: Poste): void {
    this._saving$.next(true);
    this._saved$.next(false);
    this.http
      .delete(`${environment.api()}/poste/id/${p.id}`)
      .subscribe((fo: OF) => {
        this._saving$.next(false);
        this._saved$.next(true);
        this._saved$.next(false);
        this._refreshOf$.next(new Date().getTime());
        this.snackService.success(`Opé annulée`);
      });
  }

  reloadCurrentPoste() {
    let current;

    this.currentPoste$
      .subscribe((t) => {
        current = t;
      })
      .unsubscribe();

    current &&
      this.http
        .get<Poste>(`${environment.api()}/poste/id/${current.id}`)
        .subscribe((p) => {
          this._currentPoste$.next(p);
        });
  }

  loadProdFastNav() {
    return this.http
      .get(`${environment.api()}/of/nav`)
      .subscribe((res: { fo: OF; doc: DocumentModel }[]) => {
        this._prod_nav$.next(
          res.sort((a, b) => {
            if (a.fo.numero > b.fo.numero) {
              return -1;
            } else if (a.fo.numero < b.fo.numero) {
              return 1;
            } else {
              return 0;
            }
          }),
        );
      });
  }

  loadAll(): void {
    this.http.get<OF[]>(`${environment.api()}/of`).subscribe((ofs) => {
      this._ofs$.next([...(this.mock as any as OF[]), ...ofs]);
    });
  }

  loadAllForLigne(ligneId: string) {
    this.http
      .get<OF[]>(`${environment.api()}/of/ligneId/${ligneId}`)
      .subscribe((ofs) => {
        this._ofs$.next(ofs);
      });
  }

  _loadEnAttenteInfoPDC() {
    return this.http.get<OF[]>(`${environment.api()}/of/en_attente_pdc`);
  }

  _loadEnAttenteValidation() {
    return this.http.get<OF[]>(`${environment.api()}/of/en_attente_validation`);
  }

  _loadEnAttenteValidationQualite() {
    return this.http.get<OF[]>(
      `${environment.api()}/of/en_attente_validation_qualite`,
    );
  }

  _loadEagerById(ofId: string) {
    return this.http.get<OF>(`${environment.api()}/of/id/eager/${ofId}`);
  }

  loadEagerById(ofId: string): void {
    this._ofLoaded$.next(false);
    this._ofLoading$.next(true);
    this._loadEagerById(ofId).subscribe((of) => {
      this._ofLoading$.next(false);
      this._ofLoaded$.next(true);
      this._ofLoaded$.next(false);
      this._refreshOf$.next(new Date().getTime());
    });
  }

  async firstStart_(ofId: string) {
    try {
      const of = await safeFirstValueFrom(
        this.http.post<OF>(`${environment.api()}/of/first_start/${ofId}`, null),
      );
      this._refreshOf$.next(new Date().getTime());
      this.snackService.success(`OF démarré`);
      return of;
    } catch (e) {}
  }

  loadAllToPlan(): void {
    this.http
      .get<OF[]>(`${environment.api()}/of/to/plannif`)
      .subscribe((ofs) => {
        this._ofs$.next(ofs);
      });
  }

  addOfToPlan(of: OF): void {
    this._ofs$.next([...this._ofs$.value, of]);
  }

  cancel(a: OF): void {
    this._ofSaving$.next(true);
    this.http
      .post(`${environment.api()}/of/cancel/${a.id}`, null)
      .subscribe((_) => {
        this._ofSaving$.next(false);
        this.loadProdFastNav();
        this.snackService.success(`OF annulé`);
      });
  }

  saveBilanAndPoste(a: OF): void {
    this._ofSaving$.next(true);
    this._ofSaved$.next(false);
    this.http
      .post(`${environment.api()}/poste/bilans`, a)
      .subscribe((savedOF: OF) => {
        this._ofSaving$.next(false);
        this._ofSaved$.next(true);
        this._ofSaved$.next(false);
        this._refreshOf$.next(new Date().getTime());
        this.snackService.success(`Bilans matières enregistrés`);
      });
  }
}
